import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Plus, Search, Trash2, Edit, Grid, List, Calendar, FolderOpen, Cloud, Loader2, Presentation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Header } from '../components/Header';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const toolLayoutRoutes = {
  'competency-validation': '/tools/competency',
  attestation: '/tools/attestation',
  'consent-form': '/tools/consent',
  'door-sign': '/tools/door-sign',
  sbar: '/tools/sbar',
  'poster-studio': '/tools/poster',
  certificate: '/tools/certificate',
  'badge-buddy': '/tools/badge-buddy',
  'tent-card': '/tools/tent-card',
  'safety-display': '/tools/safety-display',
};

const typeLabel = { poster: 'Poster', certificate: 'Certificate', 'door-sign': 'Door Sign', 'badge-buddy': 'Badge Buddy', 'tent-card': 'Tent Card', 'safety-display': 'Safety Board' };

function getEditHref(op) {
  const layout = op?.layout || op?.docType || '';
  const id = op?.id ?? '';
  const toolPath = toolLayoutRoutes[layout];
  return toolPath ? `${toolPath}?id=${id}` : `/editor/${id}`;
}

export default function Library() {
  const { user } = useAuth();
  const [onePagers, setOnePagers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOnePagers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/onepagers');
      setOnePagers(res.data || []);
    } catch { setOnePagers([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOnePagers(); }, [fetchOnePagers]);

  const handleDelete = async (id, title) => {
    try {
      await api.delete(`/onepagers/${id}`);
      setOnePagers((prev) => prev.filter((p) => p.id !== id));
      toast({ title: 'Deleted', description: `"${title}" removed from your library.` });
    } catch {
      toast({ title: 'Error', description: 'Could not delete the document.', variant: 'destructive' });
    }
  };

  const filtered = onePagers.filter((op) =>
    (op?.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (op?.subtitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()));

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } };

  return (
    <>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Documents</h1>
            <p className="text-gray-600 mt-1 flex items-center gap-1">{loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading...</> : <>{onePagers.length} saved document{onePagers.length !== 1 ? 's' : ''}<Cloud className="w-3 h-3 inline ml-1 text-teal-600" /></>}</p>
          </div>
          <Link to="/create"><Button className="gap-2"><Plus className="w-4 h-4" /> Create New</Button></Link>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search documents..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
          <div className="flex border rounded-lg">
            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="icon" onClick={() => setViewMode('grid')} className="rounded-r-none"><Grid className="w-4 h-4" /></Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" onClick={() => setViewMode('list')} className="rounded-l-none"><List className="w-4 h-4" /></Button>
          </div>
        </div>

        {!loading && filtered.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center"><FolderOpen className="w-10 h-10 text-gray-400" /></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{searchQuery ? 'No matches found' : 'No documents yet'}</h2>
            <p className="text-gray-600 mb-6">{searchQuery ? 'Try a different search term' : 'Create your first document to get started'}</p>
            {!searchQuery && <Link to="/create"><Button className="gap-2"><Plus className="w-4 h-4" /> Create One-Pager</Button></Link>}
          </motion.div>
        )}

        {viewMode === 'grid' && filtered.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((op, idx) => (
                <motion.div key={op.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ delay: idx * 0.03 }}>
                  <Card className="group h-full flex flex-col">
                    <CardHeader className="pb-2"><CardTitle className="text-base line-clamp-2">{op.title || 'Untitled'}</CardTitle>{op.subtitle && <CardDescription className="line-clamp-1">{op.subtitle}</CardDescription>}</CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <div className="flex-1 min-h-[120px] rounded-lg mb-3 flex flex-col items-center justify-center gap-1 relative" style={{ backgroundColor: op.colorScheme?.background || '#F0FDFA' }}>
                        {op.layout === 'poster-studio' ? <Presentation className="w-10 h-10" style={{ color: op.colorScheme?.primary || '#0D9488' }} /> : <FileText className="w-10 h-10" style={{ color: op.colorScheme?.primary || '#0D9488' }} />}
                        {typeLabel[op.docType] && <span className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/80" style={{ color: op.colorScheme?.primary || '#0D9488' }} data-testid="library-doc-type">{typeLabel[op.docType]}</span>}
                      </div>
                      <div className="flex items-center text-xs text-muted-foreground mb-3"><Calendar className="w-3 h-3 mr-1" />{formatDate(op.updatedAt || op.createdAt)}</div>
                      <div className="flex gap-2">
                        <Link to={getEditHref(op)} className="flex-1"><Button variant="outline" size="sm" className="w-full gap-1"><Edit className="w-3 h-3" /> Edit</Button></Link>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(op.id, op.title || 'Untitled')}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {viewMode === 'list' && filtered.length > 0 && (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((op, idx) => (
                <motion.div key={op.id} layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ delay: idx * 0.02 }}>
                  <Card className="group"><CardContent className="p-4"><div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: op.colorScheme?.background || '#F0FDFA' }}><FileText className="w-6 h-6" style={{ color: op.colorScheme?.primary || '#0D9488' }} /></div>
                    <div className="flex-1 min-w-0"><h3 className="font-medium text-gray-900 truncate">{op.title || 'Untitled'}</h3>{op.subtitle && <p className="text-sm text-gray-500 truncate">{op.subtitle}</p>}</div>
                    <div className="text-xs text-muted-foreground hidden sm:block">{formatDate(op.updatedAt || op.createdAt)}</div>
                    <div className="flex gap-2 shrink-0">
                      <Link to={getEditHref(op)}><Button variant="outline" size="sm" className="gap-1"><Edit className="w-3 h-3" /> Edit</Button></Link>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(op.id, op.title || 'Untitled')}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div></CardContent></Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </>
  );
}
